import type { Mindmap, MindmapLayoutDirection, MindmapNode } from './MindmapTypes';
// Use the bundled browser build to avoid requiring 'web-worker' during bundling
// This keeps ELK fully client-side without extra polyfills.
import ELK from 'elkjs/lib/elk.bundled.js';

export interface MindmapLayoutPositions {
  [nodeId: string]: { x: number; y: number };
}

// Fallback sizes used until real DOM measurements are captured from React Flow.
// Keep conservative to reduce overlap risk before measurements arrive.
const FALLBACK_NODE_WIDTH = 128; // matches min-w-[8rem]
const FALLBACK_NODE_HEIGHT = 40; // approx single-line height incl. padding

const H_GAP = 80; // spacing between layers (parent to child); visible edge length ≈ H_GAP
const V_GAP = 10; // vertical spacing between siblings (top-left coordinate spacing in ELK)

const DIRECTION_MAP: Record<MindmapLayoutDirection, 'LEFT' | 'RIGHT' | 'UP' | 'DOWN'> = {
  left: 'LEFT',
  right: 'RIGHT',
  up: 'UP',
  down: 'DOWN',
};

// Simple, synchronous tree layout (left-to-right) to avoid overlapping nodes.
// ELK integration can replace this function later with identical return shape.
export async function computeMindmapLayoutAsync(
  mindmap: Mindmap,
  direction: MindmapLayoutDirection = 'right',
  nodeDimensions?: Record<string, { width: number; height: number }>
): Promise<MindmapLayoutPositions> {
  if (!mindmap.nodes.length) return {};

  const elk = new ELK({});

  // ELK expects a single root graph with children and edges
  const elkNodes = mindmap.nodes.map((n) => {
    const dim = nodeDimensions?.[n.id];
    return {
      id: n.id,
      width: dim?.width ?? FALLBACK_NODE_WIDTH,
      height: dim?.height ?? FALLBACK_NODE_HEIGHT,
    };
  });

  const elkEdges = mindmap.nodes
    .filter((n) => n.parentId)
    .map((n) => ({ id: `e-${n.parentId}-${n.id}`, sources: [n.parentId as string], targets: [n.id] }));

  const graph = {
    id: 'root',
    children: elkNodes,
    edges: elkEdges,
    layoutOptions: {
      'elk.algorithm': 'layered',
      'elk.direction': DIRECTION_MAP[direction] ?? 'RIGHT',
      // Vertical spacing within a layer (siblings)
      'elk.spacing.nodeNode': `${V_GAP}`,
      // Horizontal spacing between layers (parent→child distance)
      'elk.layered.spacing.nodeNodeBetweenLayers': `${H_GAP}`,
      // Reduce extra padding so V_GAP drives vertical spacing more predictably
      'elk.spacing.edgeNode': '4',
      'elk.spacing.edgeEdge': '4',
      'elk.layered.spacing.edgeNodeBetweenLayers': '4',
      'elk.layered.spacing.edgeEdgeBetweenLayers': '4',
      'elk.layered.nodePlacement.bk.fixedAlignment': 'BALANCED',
      'elk.layered.considerModelOrder': 'true',
      'elk.layered.considerModelOrder.strategy': 'NODES',
      'elk.layered.crossingMinimization.forceNodeModelOrder': 'true',
    },
  } as const;

  const result = await elk.layout(graph);

  // First, collect all positions from ELK
  const positions: MindmapLayoutPositions = {};
  for (const child of result.children ?? []) {
    if (child.id != null && typeof child.x === 'number' && typeof child.y === 'number') {
      positions[child.id] = { x: child.x, y: child.y };
    }
  }

  // Do not shift Y for underline nodes — keep node Y stable regardless of shape.

  // Post-process: align nodes within their columns based on direction
  // Build depth map to group nodes by hierarchy level
  const depthMap = new Map<string, number>();
  const getDepth = (nodeId: string): number => {
    if (depthMap.has(nodeId)) return depthMap.get(nodeId)!;
    const node = mindmap.nodes.find((n) => n.id === nodeId);
    if (!node || !node.parentId) {
      depthMap.set(nodeId, 0);
      return 0;
    }
    const depth = getDepth(node.parentId) + 1;
    depthMap.set(nodeId, depth);
    return depth;
  };

  // Calculate depth for all nodes
  for (const node of mindmap.nodes) {
    getDepth(node.id);
  }

  // Group nodes by depth (nodes at same depth form a column)
  const depthGroups = new Map<number, string[]>();
  for (const nodeId of Object.keys(positions)) {
    const depth = depthMap.get(nodeId) ?? 0;
    if (!depthGroups.has(depth)) {
      depthGroups.set(depth, []);
    }
    depthGroups.get(depth)!.push(nodeId);
  }

  // Align nodes within each depth group based on direction
  for (const nodeIds of depthGroups.values()) {
    if (nodeIds.length <= 1) continue; // Single node, no alignment needed

    if (direction === 'right') {
      // Left-align: all nodes share the leftmost X
      const minX = Math.min(...nodeIds.map((id) => positions[id].x));
      for (const id of nodeIds) {
        positions[id].x = minX;
      }
    } else if (direction === 'left') {
      // Right-align: all nodes share the rightmost X (accounting for width)
      const nodeWidths = nodeIds.map((id) => {
        const dim = nodeDimensions?.[id];
        return { id, width: dim?.width ?? FALLBACK_NODE_WIDTH };
      });
      const maxRightEdge = Math.max(
        ...nodeIds.map((id) => {
          const width = nodeWidths.find((nw) => nw.id === id)?.width ?? FALLBACK_NODE_WIDTH;
          return positions[id].x + width;
        })
      );
      for (const { id, width } of nodeWidths) {
        positions[id].x = maxRightEdge - width;
      }
    } else if (direction === 'down') {
      // Top-align: all nodes share the topmost Y
      const minY = Math.min(...nodeIds.map((id) => positions[id].y));
      for (const id of nodeIds) {
        positions[id].y = minY;
      }
    } else {
      // 'up': Bottom-align: all nodes share the bottommost Y (accounting for height)
      const nodeHeights = nodeIds.map((id) => {
        const dim = nodeDimensions?.[id];
        return { id, height: dim?.height ?? FALLBACK_NODE_HEIGHT };
      });
      const maxBottomEdge = Math.max(
        ...nodeIds.map((id) => {
          const height = nodeHeights.find((nh) => nh.id === id)?.height ?? FALLBACK_NODE_HEIGHT;
          return positions[id].y + height;
        })
      );
      for (const { id, height } of nodeHeights) {
        positions[id].y = maxBottomEdge - height;
      }
    }
  }

  return positions;
}
